import { ChangeDetectionStrategy, Component, input, output, signal, computed, viewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import type { Sensor } from '../../../shared/interfaces';

@Component({
  selector: 'app-site-drawing-canvas',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DragDropModule, MatIconModule],
  templateUrl: './site-drawing-canvas.html',
})
export class SiteDrawingCanvasComponent implements OnInit, OnDestroy {
  drawingUrl = input<string | undefined>();
  sensors = input<Sensor[]>([]);
  
  positionChanged = output<{ sensorId: string; position: { x: number; y: number } }>();

  imageElement = viewChild<ElementRef<HTMLImageElement>>('drawingImg');

  imageNaturalWidth = signal(0);
  imageNaturalHeight = signal(0);
  
  scale = signal(1);
  private resizeObserver?: ResizeObserver;

  dragPositions = computed(() => {
    const s = this.scale();
    const positions = new Map<string, {x: number, y: number}>();
    for (const sensor of this.sensors()) {
      const pos = sensor.drawingPosition;
      if (!pos) {
        positions.set(sensor.id, { x: 20 * s, y: 20 * s });
      } else {
        positions.set(sensor.id, { x: pos.x * s, y: pos.y * s });
      }
    }
    return positions;
  });

  ngOnInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScale();
    });
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    this.imageNaturalWidth.set(img.naturalWidth);
    this.imageNaturalHeight.set(img.naturalHeight);
    
    if (this.resizeObserver) {
      this.resizeObserver.observe(img);
    }
    
    this.updateScale();
  }

  updateScale() {
    const img = this.imageElement()?.nativeElement;
    if (img && this.imageNaturalWidth() > 0) {
      this.scale.set(img.clientWidth / this.imageNaturalWidth());
    }
  }

  onDragEnded(event: CdkDragEnd, sensor: Sensor) {
    const dragPosition = event.source.getFreeDragPosition();
    const s = this.scale();
    if (s <= 0) return;
    
    // 元の画像座標系に変換
    const newX = Math.round(dragPosition.x / s);
    const newY = Math.round(dragPosition.y / s);

    // 変化があった場合のみ通知
    if (!sensor.drawingPosition || Math.abs(sensor.drawingPosition.x - newX) > 1 || Math.abs(sensor.drawingPosition.y - newY) > 1) {
      this.positionChanged.emit({
        sensorId: sensor.id,
        position: { x: newX, y: newY }
      });
    }
  }
}
